import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// ★★★ getUser関数をインポート ★★★
import { getAuthHeader, getUser } from "../utils/auth"; // 認証関連の関数をインポート

// --- 定数 ---
const API_URL = "https://salesbreaker.jp";
const ITEMS_PER_PAGE = 20;

// --- アイコン ---
const LoadingSpinner = () => (
    <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; // ハイライト用アイコン
// ★★★ 追加: 要素選択 (Target) アイコン ★★★
const TargetIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;


/**
 * 承認キューの詳細を表示・操作するためのモーダル
 */
function ReviewModal({ itemId, onClose, onActionSuccess }) {
    const [item, setItem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [editedMappings, setEditedMappings] = useState({});
    const iframeRef = useRef(null);
    // ★★★ 追加: 要素選択モードの状態 ★★★
    const [activeField, setActiveField] = useState(null); // 選択対象の 'key' (e.g. 'company') を保持

    // ★★★ 修正: ハイライト用スクリプト (元のコードに戻す) ★★★
    const highlightScript = `
        let highlightedElement = null;
        window.addEventListener('message', (event) => {
            // Add origin check for security if needed, but '*' might be required for srcdoc
            // if (event.origin !== "${new URL(API_URL).origin}") return;

            if (event.data && event.data.type === 'highlight') {
                if (highlightedElement) {
                    highlightedElement.style.outline = ''; // Remove previous highlight
                    highlightedElement.style.boxShadow = '';
                    highlightedElement.style.backgroundColor = '';
                    highlightedElement.style.transition = '';
                }
                try {
                    const element = document.querySelector(event.data.selector);
                    if (element) {
                        highlightedElement = element;
                        element.style.outline = '3px solid #EF4444'; // Red-500
                        element.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.5)';
                        element.style.backgroundColor = 'rgba(254, 202, 202, 0.3)'; // Red-200 with opacity
                        element.style.transition = 'outline 0.1s ease-in-out, box-shadow 0.1s ease-in-out, background-color 0.1s ease-in-out';
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        console.log('Highlighted:', event.data.selector);
                    } else {
                        console.warn('Element not found for selector:', event.data.selector);
                    }
                } catch (e) {
                    console.error('Error highlighting element:', e, 'Selector:', event.data.selector);
                }
            } else if (event.data && event.data.type === 'clearHighlight') {
                 if (highlightedElement) {
                    highlightedElement.style.outline = '';
                    highlightedElement.style.boxShadow = '';
                    highlightedElement.style.backgroundColor = '';
                    highlightedElement.style.transition = '';
                    highlightedElement = null;
                 }
            }
            // ★★★ 注意: 'SET_SELECTION_MODE' は selectorPickerScript が処理するので、ここでは 'highlight' と 'clearHighlight' のみ処理 ★★★
        });
        console.log('Highlight script ready.');
    `;

    // ★★★ 追加: 要素選択(ピッカー)用スクリプト (参照ファイルからコピー) ★★★
    const selectorPickerScript = `let hEl=null,sMA=false,b=null;function gS(e){if(!e||!e.tagName)return"";if(e.id)return"#"+e.id.replace(/(:|\\.)/g,"\\\\$1");let t="",n=e;for(;n.parentElement;){let e=n.tagName.toLowerCase(),r=Array.from(n.parentElement.children),o=r.filter(e=>e.tagName===n.tagName);o.length>1&&(e+=":nth-of-type("+(o.indexOf(n)+1)+")"),t=e+(t?" > "+t:"");try{if(document.querySelector(t)===n)return t}catch(e){}n=n.parentElement}return t}function h(e){hEl&&(hEl.style.outline="",hEl.style.boxShadow=""),e&&(e.style.outline="3px solid #3b82f6",e.style.boxShadow="0 0 10px rgba(59, 130, 246, 0.7)"),hEl=e}function tSM(e){sMA=e,e?(b||(b=document.createElement("div"),b.textContent="マッピングしたい要素をクリックしてください",Object.assign(b.style,{position:"fixed",top:"10px",left:"50%",transform:"translateX(-50%)",backgroundColor:"rgba(0,0,0,0.8)",color:"white",padding:"10px 20px",borderRadius:"8px",zIndex:"99999",fontSize:"14px",pointerEvents:"none"}),document.body.appendChild(b)),b.style.display="block",document.body.style.cursor="crosshair"):(b&&(b.style.display="none"),document.body.style.cursor="default",h(null))}window.addEventListener("message",e=>{if(e.data && e.data.type === "SET_SELECTION_MODE")tSM(e.data.isActive)});document.body.addEventListener("mouseover",e=>{sMA&&h(e.target)});document.body.addEventListener("mouseout",()=>{sMA&&h(null)});document.body.addEventListener("click",e=>{if(!sMA)return;e.preventDefault(),e.stopPropagation();const t=gS(e.target);window.parent.postMessage({type:"SELECTOR_CLICKED",selector:t},"*"),tSM(!1)},!0);console.log('Selector picker script ready.');`;


    // 詳細データ取得 (変更なし)
    useEffect(() => {
        const fetchDetail = async () => {
            setIsLoading(true);
            setError(null);
            setEditedMappings({});
            try {
                const res = await fetch(`${API_URL}/api/admin/learning-queue/${itemId}`, {
                    headers: getAuthHeader()
                });
                if (!res.ok) {
                     const errorData = await res.json().catch(() => ({}));
                     throw new Error(errorData.error || `詳細データの取得に失敗しました (Status: ${res.status})`);
                }
                const data = await res.json();
                if (data.success && data.data) {
                    setItem(data.data);
                    // マッピングデータの初期化 (変更なし)
                    if (data.data.mapping_json) {
                         let initialMappings = {};
                         try {
                            let parsedJson;
                            if (typeof data.data.mapping_json === 'string') {
                                parsedJson = JSON.parse(data.data.mapping_json);
                            } else if (typeof data.data.mapping_json === 'object' && data.data.mapping_json !== null) {
                                parsedJson = data.data.mapping_json;
                            } else {
                                throw new Error("Invalid mapping_json format received");
                            }

                            const expectedKeys = ['company', 'name', 'last_name', 'first_name', 'last_name_kana', 'first_name_kana', 'email', 'tel', 'postal_code', 'prefecture', 'address1', 'address2', 'subject', 'message', 'privacy_checkbox', 'inquiry_type'];
                            expectedKeys.forEach(key => initialMappings[key] = '');

                            if (parsedJson.field_mappings && Array.isArray(parsedJson.field_mappings)) {
                                parsedJson.field_mappings.forEach(mapping => {
                                    if(mapping.key && expectedKeys.includes(mapping.key)) {
                                      initialMappings[mapping.key] = mapping.selector || '';
                                    }
                                });
                            }
                         } catch (e) {
                             console.error("Error parsing/initializing mapping_json:", e);
                             setError("マッピングデータの解析または初期化に失敗しました。");
                         }
                         setEditedMappings(initialMappings);
                    } else {
                         const expectedKeys = ['company', 'name', 'last_name', 'first_name', 'last_name_kana', 'first_name_kana', 'email', 'tel', 'postal_code', 'prefecture', 'address1', 'address2', 'subject', 'message', 'privacy_checkbox', 'inquiry_type'];
                         let initialMappings = {};
                         expectedKeys.forEach(key => initialMappings[key] = '');
                         setEditedMappings(initialMappings);
                    }
                } else {
                    throw new Error(data.error || 'API succeeded but no data found.');
                }
            } catch (err) {
                 console.error("Fetch Detail Error:", err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDetail();
    }, [itemId]);

    // ★★★ 修正: 入力フィールド変更ハンドラを useCallback でラップ ★★★
    const handleMappingChange = useCallback((key, value) => {
        setEditedMappings(prev => ({ ...prev, [key]: value }));
    }, []); // 依存なし (setEditedMappings は安定)

    // ★★★ 修正: iframe スクリプト注入 & メッセージリスナー ★★★
    // 1. メッセージリスナーを useCallback で定義
    const handleIframeMessage = useCallback((event) => {
         const iframe = iframeRef.current;
         // event.source チェックを追加
         if (!iframe || event.source !== iframe.contentWindow) return;

         // ★★★ 追加: セレクタークリックの受信 ★★★
         if (event.data && event.data.type === 'SELECTOR_CLICKED') {
            if (activeField) {
                console.log(`Received selector "${event.data.selector}" for field "${activeField}"`);
                handleMappingChange(activeField, event.data.selector);
                setActiveField(null); // 選択モードを解除
            } else {
                console.warn("Received a selector click, but no field was active.");
            }
         }
         // (既存の highlightError ロジックはあれば残す)
         else if (event.data && event.data.type === 'highlightError') {
             console.warn(`Highlight error from iframe for selector "${event.data.selector}":`, event.data.error);
         }
    }, [activeField, handleMappingChange]); // activeField と handleMappingChange に依存

    // 2. メッセージリスナーを window に登録
    useEffect(() => {
        window.addEventListener('message', handleIframeMessage);
        return () => {
            window.removeEventListener('message', handleIframeMessage);
        };
    }, [handleIframeMessage]); // handleIframeMessage の変更時にリスナーを再登録

    // 3. iframe の load イベントリスナー (スクリプト注入)
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const handleLoad = () => {
            console.log("Iframe loaded.");
            try {
                if (!iframe.contentDocument) {
                    console.error("Cannot access iframe contentDocument.");
                    setError("スナップショットの内容にアクセスできません。");
                    return;
                }
                const scriptEl = iframe.contentDocument.createElement('script');
                // ★★★ 修正: 両方のスクリプトを注入 ★★★
                scriptEl.textContent = highlightScript + "\n\n" + selectorPickerScript;
                iframe.contentDocument.body.appendChild(scriptEl);
                console.log("Highlight and Picker scripts injected into iframe.");
            } catch (e) {
                console.error("Error injecting script or accessing iframe content:", e);
                setError(`スナップショットの操作準備中にエラーが発生しました: ${e.message}`);
            }
        };

        if (item && item.page_html_snapshot) {
            console.log("Setting srcdoc for iframe...");
            iframe.srcdoc = item.page_html_snapshot;
        } else if (!isLoading) {
             console.log("No snapshot data available to load into iframe.");
        }

        iframe.addEventListener('load', handleLoad);

        return () => {
             console.log("Cleaning up iframe load listener.");
            iframe.removeEventListener('load', handleLoad);
             if(iframe.srcdoc) { iframe.srcdoc = ''; }
        };
        // ★★★ 修正: selectorPickerScript も依存配列に追加 ★★★
    }, [item, isLoading, highlightScript, selectorPickerScript]);

    // ★★★ 追加: 選択モードを iframe に通知する useEffect ★★★
    useEffect(() => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
            // iframe がロード完了していることを確認
            if (iframeRef.current.srcdoc || item?.page_html_snapshot) {
                iframeRef.current.contentWindow.postMessage({
                    type: 'SET_SELECTION_MODE',
                    isActive: activeField !== null
                }, '*');
                console.log(`Picker mode ${activeField !== null ? 'ENABLED' : 'DISABLED'} for field: ${activeField}`);
            }
        }
    }, [activeField, item]); // item も依存に加え、iframeロード後に実行されるようにする

    // ★★★ 追加: 要素選択ボタンのハンドラ ★★★
    const handleSelectClick = (key) => {
        setActiveField(prev => (prev === key ? null : key)); // トグル（同じボタンを押したら解除）
        clearHighlight(); // ハイライトを消す
    };


    // ハイライトメッセージ送信 (変更なし)
    const handleHighlight = (selector) => {
        if (iframeRef.current && iframeRef.current.contentWindow && selector) {
            console.log(`Posting highlight message for selector: ${selector}`);
            iframeRef.current.contentWindow.postMessage({ type: 'highlight', selector: selector }, '*');
        } else {
            console.warn("Cannot highlight: Iframe not ready or selector is empty.");
        }
    };

    // ハイライト解除 (変更なし)
     const clearHighlight = useCallback(() => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
             console.log("Posting clearHighlight message.");
            iframeRef.current.contentWindow.postMessage({ type: 'clearHighlight' }, '*');
        }
    }, []);

    // --- ★★★ 承認・却下アクションハンドラ (修正) ★★★ ---
    const handleAction = async (action) => {
        // ... (既存の handleAction の内容) ...
        setIsSubmitting(true);
        setError(null);
        // 現在ログインしているユーザー情報を取得
        const currentUser = getUser();
        if (!currentUser || !currentUser.id) {
            setError("ユーザー情報が見つかりません。再ログインしてください。");
            setIsSubmitting(false);
            return;
        }
        const currentUserId = currentUser.id; // ログインユーザーのIDを使用

        // 却下の場合は専用APIを叩く
        if (action === 'reject') {
            try {
                const endpoint = `${API_URL}/api/admin/learning-queue/reject`;
                const bodyPayload = { id: itemId }; // キューのIDを 'id' として送信
                const res = await fetch(endpoint, {
                    method: 'POST',
                    headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                    body: JSON.stringify(bodyPayload)
                });
                const data = await res.json();
                if (!res.ok || !data.success) throw new Error(data.error || '却下処理に失敗しました。');
                onActionSuccess(action);
            } catch (err) {
                console.error(`Action ${action} failed:`, err);
                setError(err.message);
                setIsSubmitting(false);
            }
            return; // 却下の場合はここで終了
        }

        // --- 承認処理 (修正された /api/feedback/submit を使用) ---
        if (action === 'approve') {
            try {
                const endpoint = `${API_URL}/api/feedback/submit`;
                // 編集されたマッピングデータを整形
                const finalMappings = {
                    // source はバックエンド側で "human_review" を設定するので、ここでは不要
                    field_mappings: Object.entries(editedMappings)
                        .filter(([key, selector]) => selector && selector.trim() !== '')
                        .map(([key, selector]) => ({ key, selector: selector.trim() }))
                };

                // ★★★ bodyPayload を修正 ★★★
                const bodyPayload = {
                    userId: currentUserId, // 現在ログインしている管理者ID
                    queueId: itemId,       // 承認するキューのIDを 'queueId' として送信
                    correctedMappings: finalMappings // 修正されたマッピング
                    // configId は送信しない
                };

                const res = await fetch(endpoint, {
                    method: 'POST',
                    headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                    body: JSON.stringify(bodyPayload)
                });
                const data = await res.json();
                if (!res.ok || !data.success) throw new Error(data.error || '承認処理（フィードバック送信）に失敗しました。');

                // ★★★ 2回目のAPI呼び出し (approve) は不要になったので削除 ★★★

                onActionSuccess(action); // 親に成功を通知

            } catch (err) {
                console.error(`Action ${action} failed:`, err);
                setError(err.message);
                setIsSubmitting(false); // エラー時のみ解除
            }
        }
    };
    // --- ★★★ 承認・却下アクションハンドラ ここまで ★★★ ---


    const handleApprove = () => handleAction('approve');
    const handleReject = () => handleAction('reject');

    // モーダル外クリックハンドラ (変更なし)
    const handleCloseModal = useCallback(() => {
        clearHighlight();
        onClose();
    }, [onClose, clearHighlight]);

    // JSX
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-start gap-4 p-4 border-b">
                    <h2 className="min-w-0 text-lg sm:text-xl font-bold text-gray-800 break-words">
                        解析ルール改善レビュー ({item?.hostname || `ID: ${itemId}`})
                        {/* ★★★ 追加: 選択モードの表示 ★★★ */}
                        {activeField && (
                            <span className="mt-2 inline-block text-sm font-medium text-white bg-green-500 px-3 py-1 rounded-full animate-pulse sm:ml-4 sm:mt-0">
                                要素選択中 (対象: {activeField})
                            </span>
                        )}
                    </h2>
                    <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">&times;</button>
                </div>

                {/* Content Area (Grid) */}
                <div className="flex-grow p-4 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-hidden h-full">
                    {/* Left Panel: Iframe */}
                    <div className="flex flex-col overflow-hidden h-full">
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">ページスナップショット</h3>
                        {isLoading && (
                             <div className="flex-grow flex items-center justify-center bg-gray-100 rounded-md">
                                 <LoadingSpinner />
                             </div>
                        )}
                         <iframe
                             ref={iframeRef}
                             title="HTML Snapshot"
                             className={`w-full h-full border border-gray-300 rounded-md ${isLoading ? 'hidden' : ''} ${activeField ? 'cursor-crosshair' : ''}`} // ★ 選択中はカーソル変更
                             sandbox="allow-same-origin allow-scripts"
                         />
                        {!isLoading && error && !item?.page_html_snapshot && (
                            <div className="flex-grow flex items-center justify-center bg-red-50 border border-red-200 rounded-md p-4">
                                <p className="text-red-600 font-medium">スナップショットの読み込み/表示に失敗しました: {error}</p>
                            </div>
                        )}
                    </div>

                    {/* Right Panel: Mapping Form */}
                    <div className="flex flex-col overflow-hidden h-full">
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">抽出/編集 マッピング (CSSセレクター)</h3>
                        {isLoading && (
                             <div className="flex-grow flex items-center justify-center bg-gray-100 rounded-md">
                                 <LoadingSpinner />
                             </div>
                        )}
                        {/* マッピング編集フォーム */}
                        {!isLoading && item && (
                            <div className="flex-grow bg-gray-50 border border-gray-200 p-3 rounded-md overflow-y-auto space-y-3 text-sm">
                                {Object.keys(editedMappings).length > 0 ? (
                                    Object.entries(editedMappings).map(([key, selector]) => (
                                        <div key={key} className="flex items-center gap-1.5"> {/* gap-1.5 に */}
                                            <label htmlFor={`mapping-${key}`} className="w-28 font-medium text-gray-600 shrink-0 capitalize">{key.replace(/_/g, ' ')}:</label>
                                            <input
                                                type="text"
                                                id={`mapping-${key}`}
                                                value={selector}
                                                onChange={(e) => handleMappingChange(key, e.target.value)}
                                                className="flex-grow px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-xs font-mono"
                                                placeholder="CSS Selector (例: #name, [name='email'])"
                                                // ★★★ 修正: 選択モード中は無効化 ★★★
                                                disabled={isSubmitting || activeField !== null}
                                            />
                                            {/* ★★★ 修正: ハイライトボタン ★★★ */}
                                            <button
                                                type="button"
                                                onClick={() => handleHighlight(selector)}
                                                onMouseLeave={clearHighlight}
                                                title="スナップショット上でハイライト"
                                                className="p-1.5 text-gray-500 hover:text-indigo-600 disabled:text-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                // ★★★ 修正: 選択モード中は無効化 ★★★
                                                disabled={isSubmitting || !selector || activeField !== null}
                                            >
                                                <SearchIcon />
                                            </button>
                                            {/* ★★★ 追加: 要素選択ボタン ★★★ */}
                                            <button
                                                type="button"
                                                onClick={() => handleSelectClick(key)}
                                                title="スナップショット上で要素を選択"
                                                className={`p-1.5 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:text-gray-300
                                                    ${activeField === key
                                                        ? 'bg-green-100 text-green-700 animate-pulse'
                                                        : 'text-gray-500 hover:text-indigo-600'
                                                    }
                                                `}
                                                disabled={isSubmitting}
                                            >
                                                <TargetIcon />
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                     <p className="text-gray-500 italic p-4 text-center">このフォーム要素のマッピングデータはまだありません。</p>
                                )}
                            </div>
                        )}
                         {!isLoading && error && (
                            <div className="flex-grow flex items-center justify-center bg-red-50 border border-red-200 rounded-md p-4">
                               <p className="text-red-600 font-medium">マッピングデータの読み込み/解析に失敗しました: {error}</p>
                           </div>
                         )}
                    </div>
                </div>

                {/* Footer */}
                 <div className="p-4 border-t flex justify-end items-center gap-4 bg-gray-50 rounded-b-lg">
                     {isSubmitting && error && <p className="text-red-500 text-sm mr-auto animate-pulse">エラー: {error}</p>}
                    <button
                        onClick={handleReject}
                        // ★★★ 修正: 選択モード中は無効化 ★★★
                        disabled={isSubmitting || isLoading || activeField !== null}
                        className="flex items-center gap-2 bg-gray-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <XIcon /> 却下
                    </button>
                    <button
                        onClick={handleApprove}
                        // ★★★ 修正: 選択モード中は無効化 ★★★
                        disabled={isSubmitting || isLoading || Object.keys(editedMappings).length === 0 || activeField !== null}
                        className="flex items-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? <LoadingSpinner /> : <CheckIcon />}
                        承認して改善に反映する
                    </button>
                </div>
            </div>
        </div>
    );
}


// --- Pagination コンポーネント (変更なし) ---
function Pagination({ currentPage, totalPages, onPageChange, isLoading }) {
     if (!totalPages || totalPages <= 1) return null;

    const handlePageClick = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    return (
        <div className="flex items-center justify-center space-x-1 sm:space-x-2 mt-4">
            <button
                onClick={() => handlePageClick(1)}
                disabled={currentPage === 1 || isLoading}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                « 最初
            </button>
            <button
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                 className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
           >
                ‹ 前へ
            </button>
            <span className="text-xs sm:text-sm text-gray-700 px-2">
                {currentPage} / {totalPages}
            </span>
            <button
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                 className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
           >
                次へ ›
            </button>
            <button
                onClick={() => handlePageClick(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                 className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
           >
                最後 »
            </button>
        </div>
    );
}

// --- メインコンポーネント (変更なし) ---
export default function AdminLearningQueue() {
     const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ totalPages: 0, totalItems: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);

     const fetchQueue = useCallback(async (page, status) => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ status, page, limit: ITEMS_PER_PAGE });
            const res = await fetch(`${API_URL}/api/admin/learning-queue?${params.toString()}`, {
                headers: getAuthHeader()
            });

            if (res.status === 401) {
                 throw new Error('認証が必要です。再度ログインしてください。');
            }
            if (!res.ok) {
                 const errorText = await res.text();
                 console.error("Fetch error response text:", errorText);
                 let errorJson = {};
                 try { errorJson = JSON.parse(errorText); } catch(e) {/* ignore */}
                 throw new Error(errorJson.error || `データの取得に失敗しました (ステータス: ${res.status})`);
            }
            const data = await res.json();
            if (data.success) {
                setItems(data.data || []);
                setPagination(data.pagination || { totalPages: 0, totalItems: 0 });
            } else {
                throw new Error(data.error || 'APIは成功を返しませんでした。');
            }
        } catch (err) {
            console.error("Fetch Queue Error:", err);
            setError(err.message);
             setItems([]);
             setPagination({ totalPages: 0, totalItems: 0 });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQueue(currentPage, statusFilter);
    }, [currentPage, statusFilter, fetchQueue]);

    const handleTabChange = (status) => {
        if (status !== statusFilter) {
             setStatusFilter(status);
             setCurrentPage(1);
        }
    };

    const handlePageChange = (newPage) => {
         if (newPage !== currentPage && newPage > 0 && pagination && newPage <= pagination.totalPages) {
             setCurrentPage(newPage);
         }
    };

     const handleActionSuccess = useCallback((action) => {
        console.log(`アクション「${action}」が成功しました。リストを更新します。`);
        setSelectedItemId(null);
        fetchQueue(currentPage, statusFilter);
    }, [currentPage, statusFilter, fetchQueue]);


    const tabs = [
        { status: 'pending', label: '承認待ち' },
        { status: 'approved', label: '承認済み' },
        { status: 'rejected', label: '却下済み' },
    ];

     const totalItemsCount = pagination?.totalItems || 0;

    return (
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <div className="flex flex-col gap-2 mb-6 sm:flex-row sm:justify-between sm:items-center">
                 <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">拡張機能 解析ルール改善キュー</h1>
                 <span className="text-sm text-gray-500">
                     {statusFilter === 'pending' ? '承認待ち' : statusFilter === 'approved' ? '承認済み' : '却下済み'} : {totalItemsCount}件
                 </span>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex gap-6 sm:gap-8 overflow-x-auto" aria-label="Tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.status}
                            onClick={() => handleTabChange(tab.status)}
                            disabled={isLoading}
                            className={`
                                ${statusFilter === tab.status
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                                whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded-t-md
                            `}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded relative mb-4 shadow-md" role="alert">
                    <strong className="font-bold block sm:inline">エラー: </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                        <LoadingSpinner />
                        <span className="ml-3 text-lg font-medium text-gray-600 animate-pulse">読み込み中...</span>
                    </div>
                )}
                <div>
                    <div className={`min-h-[300px] ${isLoading ? 'opacity-50 blur-sm' : ''} transition-opacity duration-300`}>
                        <div className="divide-y divide-gray-100 md:hidden">
                            {!isLoading && items.length === 0 && !error && (
                                <div className="p-8 sm:p-12 text-center text-gray-500 italic">
                                    {statusFilter === 'pending' ? '承認待ち' : statusFilter === 'approved' ? '承認済み' : '却下済み'} のデータはありません。
                                </div>
                            )}
                            {!isLoading && items.length > 0 && items.map((item) => (
                                <div key={`${item.id}-mobile`} className="p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="break-words font-semibold text-gray-900">{item.hostname || '-'}</p>
                                            <a href={item.target_url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-all text-xs text-blue-600 underline">
                                                {item.target_url || '-'}
                                            </a>
                                        </div>
                                        <span className={`shrink-0 px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize shadow-sm
                                            ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                             item.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-200' :
                                             item.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-gray-100 text-gray-800 border border-gray-200'
                                            }`}
                                        >
                                            {item.status === 'pending' ? '承認待ち' : item.status === 'approved' ? '承認済' : item.status === 'rejected' ? '却下済' : (item.status || '不明')}
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-1 text-xs text-gray-600">
                                        <p><span className="font-semibold text-gray-500">提出日時:</span> {item.created_at ? new Date(item.created_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'}) : '-'}</p>
                                        <p className="break-all"><span className="font-semibold text-gray-500">クライアントID:</span> {item.client_id || '-'}</p>
                                        <p><span className="font-semibold text-gray-500">優先度:</span> {Number(item.priority_score || 0)}</p>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {item.preview_focus && (
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{item.preview_focus}</span>
                                        )}
                                        {Number(item.field_mapping_count || 0) > 0 && (
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 border border-gray-200">項目 {item.field_mapping_count}</span>
                                        )}
                                        {Number(item.choice_option_count || 0) > 0 && (
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-sky-50 text-sky-700 border border-sky-100">選択肢 {item.choice_option_count}</span>
                                        )}
                                        {Number(item.custom_input_count || 0) > 0 && (
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">固定入力 {item.custom_input_count}</span>
                                        )}
                                        {Number(item.has_snapshot) === 1 && (
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">スナップあり</span>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setSelectedItemId(item.id)}
                                        className="mt-4 flex w-full items-center justify-center gap-1 rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-600"
                                        disabled={isLoading}
                                    >
                                        <EyeIcon /> 詳細
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="hidden overflow-x-auto md:block">
                        <table className="w-full text-sm text-left text-gray-600">
                            <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0 z-5">
                                <tr>
                                    <th className="py-3 px-4">提出日時</th>
                                    <th className="py-3 px-4">クライアントID</th>
                                    <th className="py-3 px-4">ホスト名</th>
                                    <th className="py-3 px-4">送信先URL</th>
                                    <th className="py-3 px-4">プレビュー</th>
                                    <th className="py-3 px-4">ステータス</th>
                                    <th className="py-3 px-4">操作</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {/* No Data Message */}
                                {!isLoading && items.length === 0 && !error && (
                                    <tr>
                                        <td colSpan="7" className="text-center py-16 text-gray-500 italic">
                                            {statusFilter === 'pending' ? '承認待ち' : statusFilter === 'approved' ? '承認済み' : '却下済み'} のデータはありません。
                                        </td>
                                    </tr>
                                )}
                                {/* Data Rows */}
                                {!isLoading && items.length > 0 && items.map((item) => (
                                    <tr key={item.id} className="hover:bg-indigo-50 transition-colors duration-150 ease-in-out">
                                        <td className="py-3 px-4 whitespace-nowrap">{item.created_at ? new Date(item.created_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'}) : '-'}</td>
                                        <td className="py-3 px-4 font-mono text-xs">{item.client_id || '-'}</td>
                                        <td className="py-3 px-4 font-medium text-gray-800">{item.hostname}</td>
                                        <td className="py-3 px-4 max-w-xs truncate">
                                            <a href={item.target_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline hover:text-blue-800 transition-colors" title={item.target_url}>
                                                {item.target_url}
                                            </a>
                                        </td>
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-xs font-semibold text-gray-700">優先 {Number(item.priority_score || 0)}</span>
                                                <div className="flex flex-wrap gap-1">
                                                    {item.preview_focus && (
                                                        <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{item.preview_focus}</span>
                                                    )}
                                                    {Number(item.field_mapping_count || 0) > 0 && (
                                                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 border border-gray-200">項目 {item.field_mapping_count}</span>
                                                    )}
                                                    {Number(item.choice_option_count || 0) > 0 && (
                                                        <span className="px-2 py-0.5 text-xs rounded-full bg-sky-50 text-sky-700 border border-sky-100">選択肢 {item.choice_option_count}</span>
                                                    )}
                                                    {Number(item.custom_input_count || 0) > 0 && (
                                                        <span className="px-2 py-0.5 text-xs rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">固定入力 {item.custom_input_count}</span>
                                                    )}
                                                    {Number(item.has_snapshot) === 1 && (
                                                        <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">スナップあり</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full capitalize shadow-sm
                                                ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                                 item.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-200' :
                                                 item.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-gray-100 text-gray-800 border border-gray-200'
                                                }`}
                                            >
                                                 {item.status === 'pending' ? '承認待ち' : item.status === 'approved' ? '承認済' : item.status === 'rejected' ? '却下済' : (item.status || '不明')}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <button
                                                onClick={() => setSelectedItemId(item.id)}
                                                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-semibold disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded"
                                                disabled={isLoading}
                                           >
                                                <EyeIcon /> 詳細
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>
                 {/* Pagination */}
                 {pagination && pagination.totalPages > 0 && !isLoading && (
                     <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                         <Pagination
                             currentPage={currentPage}
                             totalPages={pagination.totalPages}
                             onPageChange={handlePageChange}
                             isLoading={isLoading}
                         />
                     </div>
                 )}
            </div>

            {/* Review Modal */}
            {selectedItemId && (
                <ReviewModal
                    itemId={selectedItemId}
                    onClose={() => setSelectedItemId(null)}
                    onActionSuccess={handleActionSuccess}
               />
            )}
        </div>
    );
}
